import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ErrorsHostDirective } from '@app/core/forms/directives/errors-host.directive';
import { IfInvalidDirective } from '@app/core/forms/directives/if-invalid.directive';
import { ScrollElements } from '@app/core/forms/directives/scroll-ements';
import { ScrollToInvalidFieldOnFormSubmitDirective } from '@app/core/forms/directives/scroll-to-invalid-field-on-form-submit.directive';
import { applyErrorsToForm } from '@app/core/helpers/apply-errors-to-form';
import { extractHttpErrorResponse } from '@app/core/helpers/extract-http-error-response';
import { NewPasswordComponent } from '@app/layout/new-password/new-password.component';
import { SignUp } from '@app/store';
import { Navigate } from '@ngxs/router-plugin';
import {
  ActionCompletion,
  Actions,
  ofActionCompleted,
  Store,
} from '@ngxs/store';
import { tap } from 'rxjs';
import { SignUpReqDto } from './interfaces/sign-up.req.dto';
import { SignInWithGoogleComponent } from '../sign-in/sign-in-with-google/sign-in-with-google.component';

@Component({
  selector: 'app-sign-up-page',
  templateUrl: './sign-up.page.html',
  styleUrls: ['./sign-up.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    ScrollToInvalidFieldOnFormSubmitDirective,
    IfInvalidDirective,
    ErrorsHostDirective,
    NewPasswordComponent,
    SignInWithGoogleComponent,
  ],
})
export class SignUpPage implements OnInit, AfterViewInit {
  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private actions$: Actions,
    private destroyRef: DestroyRef,
    private cdRef: ChangeDetectorRef,
  ) {}

  @ViewChild(ScrollToInvalidFieldOnFormSubmitDirective)
  private scrollDirective!: ScrollToInvalidFieldOnFormSubmitDirective;

  @ViewChild(NewPasswordComponent)
  newPasswordComponent!: NewPasswordComponent;

  protected form!: FormGroup;
  protected ScrollElements = ScrollElements;

  protected registrationSuccessful: boolean = false;

  ngOnInit(): void {
    this.form = this.buildForm();
    this.handleBackendResponse();
    this.handleFormValueChanges();
  }

  ngAfterViewInit(): void {
    this.extendForm();
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
    });
  }

  private extendForm(): void {
    this.form.addControl('password', this.newPasswordComponent.passwordFC);
    this.form.addControl(
      'confirmPassword',
      this.newPasswordComponent.confirmPasswordFC,
    );
  }

  private handleBackendResponse(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(SignUp),
        tap((actionCompletion: ActionCompletion) => {
          if (actionCompletion.result.successful) {
            this.resetFormValue();
          } else {
            const errors = extractHttpErrorResponse(actionCompletion);
            applyErrorsToForm(errors, this.form);
            this.scrollDirective.scrollToFirstInvalidControl();
          }
          setTimeout(() => {
            this.registrationSuccessful = actionCompletion.result.successful;
            this.cdRef.detectChanges();
          });
        }),
      )
      .subscribe();
  }

  private handleFormValueChanges(): void {
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((v: any) => {
          this.registrationSuccessful = false;
        }),
      )
      .subscribe();
  }

  get emailFC(): FormControl {
    return this.form.get('email') as FormControl;
  }

  get passwordFC(): FormControl {
    return this.form.get('password') as FormControl;
  }

  get confirmPasswordFC(): FormControl {
    return this.form.get('confirmPassword') as FormControl;
  }

  private resetFormValue(): void {
    this.form.reset();
  }

  protected onSubmit(): void {
    this.registrationSuccessful = false;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const signUpData: SignUpReqDto = {
      email: this.emailFC.value,
      password: this.passwordFC.value,
    };

    this.store.dispatch(new SignUp(signUpData));
  }
}
